import { type ReactNode } from 'react'
import { cn } from '#app/utils/misc'

/**
 * The canonical section label for editorial marketing sections: a short violet
 * rule + a mono, uppercase, wide-tracked kicker. Left-aligned by default (pair
 * it with a left-aligned heading); pass `align="center"` to center the rule +
 * label together for centered sections. Prefer this over the bare `Eyebrow` for
 * new section intros so the whole marketing site shares one kicker language.
 */
export function SectionKicker({
	children,
	className,
	tone = 'brand',
	align = 'left',
}: {
	children: ReactNode
	className?: string
	tone?: 'brand' | 'brand-light' | 'muted'
	align?: 'left' | 'center'
}) {
	const color =
		tone === 'muted'
			? 'text-[#8A867D]'
			: tone === 'brand-light'
				? 'text-brand-light'
				: 'text-brand'
	const rule =
		tone === 'muted'
			? 'bg-[#8A867D]/40'
			: tone === 'brand-light'
				? 'bg-brand-light/40'
				: 'bg-brand/40'
	return (
		<div
			className={cn(
				'inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.18em] uppercase',
				color,
				align === 'center' && 'justify-center',
				className,
			)}
		>
			<span className={cn('inline-block h-px w-6', rule)} />
			{children}
		</div>
	)
}

/** Mono, uppercase, tracked eyebrow that labels each section. */
export function Eyebrow({
	children,
	className,
	tone = 'brand',
}: {
	children: ReactNode
	className?: string
	tone?: 'brand' | 'brand-light' | 'muted'
}) {
	const color =
		tone === 'muted'
			? 'text-[#8A867D]'
			: tone === 'brand-light'
				? 'text-brand-light'
				: 'text-brand'
	return (
		<div
			className={cn(
				'font-mono text-xs font-semibold tracking-[0.08em] uppercase',
				color,
				className,
			)}
		>
			{children}
		</div>
	)
}

/** Display heading in Bricolage Grotesque with the brand's tight tracking. */
export function Heading({
	children,
	className,
	as: Tag = 'h2',
}: {
	children: ReactNode
	className?: string
	as?: 'h1' | 'h2' | 'h3'
}) {
	return (
		<Tag
			className={cn(
				'font-display font-bold tracking-[-0.02em] text-balance',
				className,
			)}
		>
			{children}
		</Tag>
	)
}
